/**
 * One-time audio latency banner (W2-08 timing; DESIGN.md §5.2 "Latency calibration", A§8): on the first playback,
 * when the device's output latency (outputLatency + baseLatency) exceeds 50 ms or the browser does not report it,
 * the banner says "Not calibrated" with Calibrate and Dismiss (round-2 UX: a single uncalibrated estimate read
 * like a fact — 51, then 59, 43 and 35 ms in one session — so the banner shows no number until Calibrate runs;
 * Timing's "Audio latency" row already reads the same way). `settings.general.latencyHintShown` makes it one-time
 * (set when it appears); `watchFirstPlayback` (the module's init) decides when it shows.
 */

import type { AppServices } from '../../app/registry/types';
import { needsLatencyHint } from '../../audio/latency';
import type { Unsubscribe } from '../../core/util/types';
import { Button, Icon, IconButton } from '../../ui';
import { m } from './timing.messages';
import { openCalibration } from './timingActions';
import { useText } from './timingHooks';
import { timingSessionFor, useTimingSession, type TimingSessionState } from './timingSession';
import s from './Timing.module.css';

/** Shows the banner on the first playback that needs it (once per user). Returns the disposer. */
export function watchFirstPlayback(services: Pick<AppServices, 'audio' | 'settings' | 'editor'>): Unsubscribe {
  let wasPlaying = services.audio.transport.state.playing;
  return services.audio.transport.onChange((st) => {
    const started = st.playing && !wasPlaying;
    wasPlaying = st.playing;
    if (!started || services.settings.value.general.latencyHintShown) return;
    if (!needsLatencyHint(services.audio.detectedLatencyMs)) return;
    services.settings.set('general', 'latencyHintShown', true);
    timingSessionFor(services).set({ latencyBanner: true });
  });
}

const selectBanner = (st: TimingSessionState) => st.latencyBanner;

export function LatencyHintBanner(props: { readonly services: AppServices }) {
  const { services } = props;
  const text = useText(services);
  const visible = useTimingSession(services, selectBanner);
  if (!visible) return null;
  const ms = services.audio.detectedLatencyMs;
  // Until Calibrate has run the estimate is a guess: the banner says so instead of printing one sample of it.
  const calibrated = services.settings.value.audio.latency.calibrationMs !== 0;
  const hide = (): void => {
    timingSessionFor(services).set({ latencyBanner: false });
  };
  return (
    <div className={s.banner} role="status" data-banner="timing.latency" data-testid="timing-latency-banner">
      <Icon name="Headphones" size={16} />
      <span className={s.grow}>
        {!calibrated
          ? text.t(m.latencyNone)
          : ms === null
            ? text.t(m.hintBannerUnknown)
            : text.t(m.hintBanner, { ms: Math.round(ms) })}
      </span>
      <Button
        size="sm"
        variant="primary"
        icon="Timer"
        onClick={() => {
          hide();
          void openCalibration(services);
        }}
      >
        {text.t(m.hintCalibrate)}
      </Button>
      <IconButton icon="X" size="sm" label={{ key: m.hintDismiss }} onClick={hide} />
    </div>
  );
}
